import React, { useMemo } from 'react';
import styled from 'styled-components';

import { Tooltip } from 'shared/components';
import useHover from 'shared/hooks/use-hover';
import { PlusIcon } from 'src/dispensary-admin/customize/homepage/sections/components/plus-icon';

import { CustomNavigationItemType } from 'types/graphql';
import { customNavigationItemTypes, CustomNavigationItemTypeOption } from '../custom-navigation.constants';

type NavigationItemSelectorProps = {
  excludedTypes?: CustomNavigationItemType[];
  onAddItem: (navigationType: CustomNavigationItemType) => void;
};

export function NavigationItemSelector({ excludedTypes = [], onAddItem }: NavigationItemSelectorProps): JSX.Element {
  const availableTypes = useMemo(
    () => customNavigationItemTypes.filter((itemType) => !excludedTypes.includes(itemType.key)),
    [excludedTypes]
  );

  return (
    <Container>
      <Title>Add Navigation Items</Title>
      <OptionsGrid>
        {availableTypes.map((itemType) => (
          <NavigationItemOption key={itemType.key} itemType={itemType} onAdd={() => onAddItem(itemType.key)} />
        ))}
      </OptionsGrid>
    </Container>
  );
}

type NavigationItemOptionProps = {
  itemType: CustomNavigationItemTypeOption;
  onAdd: () => void;
};

function NavigationItemOption({ itemType, onAdd }: NavigationItemOptionProps): JSX.Element {
  const [hoverRef, isHovered] = useHover();

  return (
    <OptionRow
      ref={hoverRef}
      $hovered={isHovered}
      data-testid={`${(itemType.key as string).toLowerCase()}-navigation-item-option`}
    >
      <OptionContent>
        <OptionTitle>{itemType.title}</OptionTitle>
        {isHovered && itemType.description && (
          <Tooltip
            arrowRight='103px'
            bottom='30px'
            grey
            iconOpacity={0.7}
            left='auto'
            right='-103px'
            tooltipCopy={itemType.description}
            width='298px'
          />
        )}
      </OptionContent>
      <PlusIcon
        isHovered={isHovered}
        onClick={onAdd}
        data-testid={`${(itemType.key as string).toLowerCase()}-add-button`}
      />
    </OptionRow>
  );
}

const Container = styled.div`
  border: 1px dashed #969ea5;
  border-radius: 8px;
  margin-bottom: 24px;
  padding: 16px 0 16px 16px;
  width: 100%;
`;

const Title = styled.h3`
  color: #4f5d68;
  font-size: 14px;
  font-weight: bold;
  margin-bottom: 8px;
  padding-left: 16px;
`;

const OptionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);

  @media only screen and (max-width: 500px) {
    grid-template-columns: 1fr;
  }
`;

const OptionRow = styled.div<{ $hovered: boolean }>`
  align-items: center;
  background: ${({ $hovered }) => ($hovered ? '#f3f6f8' : 'none')};
  border-radius: 4px;
  color: ${({ $hovered }) => ($hovered ? '#454e50' : '#677882')};
  cursor: pointer;
  display: flex;
  min-height: 44px;
  justify-content: space-between;
  padding: 0 16px;
  transition: background 0.15s, color 0.15s;
  width: 100%;
`;

const OptionContent = styled.div`
  align-items: center;
  display: flex;
`;

const OptionTitle = styled.span`
  font-size: 13px;
`;
